import Button from "../../../components/Buttons/Button";
import { Form } from "./style";
import { useForm } from "react-hook-form";
import { secondary } from "../../../theme";
import { useApi } from "../../../hooks/api";
import { useNavigate } from "react-router-dom";
import { useGlobal } from "../../../hooks/global";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ModalLoading } from "../../../components/Loadings/ModalLoading";
import { IGroupProps, IOptionsProps, businessProps, franchiseProps } from "../../../interfaces";
import { GenericForm, IGenericFormRefProps } from "../../../components/GenericForm";
import { validateCEP, validateCNPJ, validateEmail, validatePhone } from "../../../utils/validatesFields";
import { managedAccommodationsOptions, positionTypeOptions } from "../../../utils/options";

interface ICompanyDataFormProps {
    defaultData?: businessProps | null;
    defaultFranchise?: businessProps | null;
}

const getDefaultResponsible = (props: any) => {
    const { defaultFranchise, user } = props
    if (defaultFranchise) return { label: defaultFranchise.responsible?.registration_data.name, value: defaultFranchise.responsible?.id } as IOptionsProps
    else if (user && ["responsible"].includes(user.type)) return { label: user?.person?.registration_data.name, value: user?.person_id } as IOptionsProps
    else return undefined
}

export const CompanyDataForm: React.FC<ICompanyDataFormProps> = (props) => {
    const { defaultData } = props

    const navigate = useNavigate();
    const { notify, theme, isActiveActions, business } = useGlobal();
    const { user, api, consultsServices } = useApi();

    const activeActions = useMemo(() => isActiveActions(business?.subscriptions?.situation), [isActiveActions, business?.subscriptions?.situation]);

    const [loadingCEP, setLoadingCEP] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);

    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors },
        setError,
        setValue,
        trigger,
    } = useForm({
        defaultValues: defaultData?.id ? defaultData : ({} as any),
        mode: "all",
    });

    const _form = watch()

    const form_ref = useRef<IGenericFormRefProps>(null);

    const changeCEP = useCallback(async (search) => {
        search = search.replace(/\D/g, "")
        if (search.length === 8) {
            setLoadingCEP(true)

            const { address, city, state } = await consultsServices.address({ search })

            if (address) {
                setValue("street", address.logradouro)
                setValue("neighborhood", address.bairro)
                setValue("address_complement", address.complemento)
            }

            if (city) setValue("city_id", { label: city.name, value: city.id, this: city })

            if (state) setValue("state_id", { label: state.name, value: state.id, initials: state.initials, this: state })

            setLoadingCEP(false)

            trigger()
        }
    }, []);

    const onSubmit = useCallback(async () => {
        setLoadingSave(true)

        try {
            const form = form_ref.current?.getForm()

            const { cnpj, email, legal_name, managed_accommodations, phone, trade_name } = form?.registration_data;
            const { address_complement, address_number, city_id, neighborhood, state_id, street, zip_code } = form?.address;

            const business = {
                legal_name,
                trade_name,
                cnpj,
                zip_code,
                phone,
                email,
                state_id,
                city_id,
                address_complement,
                neighborhood,
                address_number,
                street,
                managed_accommodations,
            }

            await api.put(`/business/${user.bus_id}`, { ...business })

            notify("Dados da empresa atualizados com sucesso.", "success")

            if (activeActions) navigate("/")
            else navigate("/dashboard")

        } catch (err: any) {
            const error = err.response ? err.response.data : "SERVER ERROR"
            if (error.path) setError(error.path, { type: "manual", message: error.message })
            else notify("ERRO INTERNO DO SISTEMA", "error")
        }

        setLoadingSave(false)
    }, [api, form_ref])

    const handleStopPropagation = useCallback((e: FormEvent<HTMLFormElement>) => {
        e?.stopPropagation()
        handleSubmit(onSubmit)(e)
    }, [handleSubmit, onSubmit, form_ref, _form, api])

    const groups: IGroupProps[] = [
        {
            name: "registration_data",
            label: "Dados de Registro",
            fields: [
                [
                    {
                        name: "legal_name",
                        label: "Razão Social",
                        type: "input",
                        required: true
                    },
                    {
                        name: "trade_name",
                        label: "Nome Fantasia",
                        type: "input",
                        required: true
                    }
                ],
                [
                    {
                        name: "cnpj",
                        label: "CNPJ",
                        type: "input",
                        mask: "cnpj",
                        required: true,
                        validate: validateCNPJ
                    },
                    {
                        name: "managed_accommodations",
                        label: "Acomodações administradas",
                        type: "select-fixed",
                        options: managedAccommodationsOptions,
                        required: true
                    },
                ],
                [
                    {
                        name: "phone",
                        label: "Telefone",
                        type: "input",
                        mask: "phone",
                        validate: validatePhone,
                        required: true
                    },
                    {
                        name: "email",
                        label: "E-mail",
                        type: "input",
                        validate: validateEmail,
                        required: true
                    },

                ]
            ]
        },
        {
            name: "address",
            label: "Dados de Endereço",
            fields: [
                [
                    {
                        name: "zip_code",
                        label: "CEP",
                        type: "input",
                        mask: "cep",
                        validate: validateCEP,
                        executeOnChange: changeCEP,
                        required: true,
                    },
                    {
                        name: "state_id",
                        label: "Estado",
                        type: "select-single-no-creatable",
                        isClearable: true,
                        searchOptions: consultsServices.state_id,
                        executeOnChange: async () => setValue("city_id", null),
                        required: true,
                    },
                    {
                        name: "city_id",
                        label: "Cidade",
                        type: "select-single-no-creatable",
                        isClearable: true,
                        searchOptions: consultsServices.city_id,
                        additionalsQueries: (data: any) => ({ state_id: data?.state_id?.value }),
                        getIsDisabled: (data: any) => !data?.state_id,
                        required: true,
                    }
                ],
                [
                    {
                        name: "neighborhood",
                        label: "Bairro",
                        type: "input",
                        required: true
                    },
                    {
                        name: "street",
                        label: "Logradouro",
                        type: "input",
                        required: true
                    },
                    {
                        name: "address_complement",
                        label: "Complemento",
                        type: "input"
                    },
                    {
                        name: "address_number",
                        label: "Número",
                        type: "input",
                        mask: "onlyNumber",
                        required: true
                    }
                ]
            ]
        }
    ]

    return (
        <>

            <Form onSubmit={handleStopPropagation} theme={theme}>

                <GenericForm
                    ref={form_ref}
                    groups={groups}
                    _form={_form}
                    control={control}
                    trigger={trigger}
                    errors={errors}
                    disabled={loadingCEP}
                    setValue={setValue}
                    register={register}
                />

                <Button
                    type="submit"
                    background={secondary[theme]}
                    color={"#FFF"}
                    style={{ padding: 8, fontSize: 14 }}
                    children={"Salvar"}
                />

            </Form>

            <ModalLoading
                loading={loadingSave}
                theme={theme}
            />

        </>

    )
}